import React, { useState, useEffect, ChangeEvent } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { CurrencyInput } from "@app/components/common/currency-converter/components/currency-input/CurrencyInput";
import { Swap } from "@app/components/common/icons";
import { RatesInfo } from "@app/components/common/currency-converter/types";
import { CurrencyInfo } from "@app/components/common/currency-converter/components/currency-info/CurrencyInfo";
import { calculateConverterAmount } from "@app/components/common/currency-converter/helpers";
import { TextSize } from "@app/components/common/currency-converter/components/currency-info/enums";
import { Currency } from "@app/types/currencies";
import { HistoricalChart } from "./components/historical-chart/HistoricalChart";

type Props = {
  baseCurrency: Currency;
  convertCurrency: Currency;
  rates: Record<Currency, RatesInfo>;
  exchangeRate: number;
  chartTitle?: string;
  footnote?: string;
  setBaseCurrency: (currency: Currency) => void;
  setConvertCurrency: (currency: Currency) => void;
};

export const Converter: React.FC<Props> = ({
  baseCurrency,
  convertCurrency,
  rates,
  exchangeRate,
  chartTitle,
  footnote,
  setBaseCurrency,
  setConvertCurrency,
}) => {
  const { t } = useTranslation("currency-converter");
  const [baseAmount, setBaseAmount] = useState<string>("1000");
  const [convertedAmount, setConvertedAmount] = useState<string>(
    calculateConverterAmount(baseAmount, exchangeRate),
  );

  const onAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBaseAmount(event.target.value);

    setConvertedAmount(
      calculateConverterAmount(event.target.value, exchangeRate),
    );
  };

  const onConverterAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    setConvertedAmount(event.target.value);

    setBaseAmount(
      calculateConverterAmount(event.target.value, 1 / exchangeRate),
    );
  };

  const onCurrencySelect =
    (setter: React.Dispatch<React.SetStateAction<Currency>>) =>
    (event: React.MouseEvent) =>
      setter(event.currentTarget.getAttribute("data-value") as Currency);

  const onSwap = () => {
    setBaseCurrency(convertCurrency);
    setConvertCurrency(baseCurrency);
  };

  useEffect(() => {
    setConvertedAmount(calculateConverterAmount(baseAmount, exchangeRate));
  }, [exchangeRate]);

  return (
    <div className="space-y-15">
      <div
        className={classNames(
          "flex flex-col items-center gap-5",
          "md:flex-row",
          "xl:gap-9",
        )}
      >
        <div className="grow">
          <CurrencyInput
            label={t("from")}
            value={baseAmount}
            currency={baseCurrency}
            onChange={onAmountChange}
            onCurrencyChange={onCurrencySelect(setBaseCurrency)}
          />
        </div>

        <div className="flex flex-row items-center justify-center">
          <div
            className={classNames(
              "bg-divider flex h-15 w-15 rotate-90 cursor-pointer flex-row items-center justify-center rounded-full",
              "hover:opacity-70",
              "md:translate-y-[30%] md:rotate-0",
            )}
            onClick={onSwap}
          >
            <Swap />
          </div>
        </div>

        <div className="grow">
          <CurrencyInput
            label={t("to")}
            value={convertedAmount}
            currency={convertCurrency}
            onChange={onConverterAmountChange}
            onCurrencyChange={onCurrencySelect(setConvertCurrency)}
          />
        </div>
      </div>
      <CurrencyInfo
        baseCurrency={baseCurrency}
        convertCurrency={convertCurrency}
        ratesInfo={rates}
        textSize={TextSize.LARGE}
      />
      <HistoricalChart
        baseCurrency={baseCurrency}
        convertCurrency={convertCurrency}
        title={chartTitle}
      />
      {footnote && (
        <p
          className={classNames(
            "text-extra-small text-base-400 hidden",
            "md:block",
          )}
        >
          {footnote}
        </p>
      )}
    </div>
  );
};
